/**
 * Single source of truth for the self-serve "Beta Features" settings list (#23066). Both the
 * settings page and any future internal tooling should read from this — adding/removing a beta
 * should be a config change here, not a new UI build.
 *
 * `flagKey` is the LaunchDarkly flag the client reads via useFlags() for the feature's current
 * on/off state; `segmentKey` is the LD segment the opt-in mutation adds/removes the company id
 * from. The flag's targeting must include a "member of segment `segmentKey`" rule for the two to
 * stay in sync — that wiring lives in LaunchDarkly's own config, not here. `flagKey: null` means
 * no client-readable flag exists yet for that feature — every feature has a confirmed flagKey as
 * of #23066, but the wiring between each flag's targeting and its segment is unconfirmed except
 * for `estimateNext` (see docs/flows/estimate-next-editor.md).
 *
 * `flagKey` must be a boolean flag: the settings page reads it as `!!flags[flagKey]`, so a
 * multi-variation flag (e.g. `translations`, which serves a language-selection string) needs its
 * own dedicated boolean flag for the opt-in gate rather than reusing the feature's own flag —
 * otherwise a falsy-looking variation (a string literally named "off") still reads as truthy.
 */

import { SUPPORTED_LANGS } from './builder/quote/util/translation-langs'

export interface BetaFeatureCheckbox {
  id: string
  label: string
}

export interface BetaFeatureConfig {
  /** Stable id, used as React key and to reference the feature (e.g. in the feedback mutation). */
  key: string
  name: string
  /** 1-2 line card description. */
  description: string
  /** LaunchDarkly segment key company ids are added to/removed from when toggled. */
  segmentKey: string | null
  /** LaunchDarkly flag key the client reads for the feature's current on/off state. */
  flagKey: string | null
  /**
   * A separate flag to force to a specific variation on enable/disable, for a feature whose real
   * gate is a flag other than `flagKey` (see `flagKey`'s multi-variation note above) and whose
   * LaunchDarkly targeting isn't wired to `segmentKey` membership. Toggling this beta then writes
   * the matching variation directly via managed per-company targeting rather than relying on LD
   * config to key off the segment — see `work-order-translations` / ENG-1677. Omit when `flagKey`'s
   * own boolean state is the real gate, or when the LD-config wiring described above is already in
   * place.
   */
  cascadeFlag?: { flagKey: string; enabledValue: any; disabledValue: any }
  /**
   * Extra LaunchDarkly segments the company joins on enable and leaves on disable, alongside
   * `segmentKey`. Use for a companion feature that ships with this beta and whose flag targeting
   * already keys off its own segment (e.g. AI presentation tools riding along with presentations v2).
   */
  cascadeSegmentKeys?: string[]
  /** Listed but not yet toggleable — toggle is disabled, "Learn More" becomes "Coming Soon!". */
  comingSoon?: boolean
  /** Gated behind the Operations add-on — toggle is replaced with an "Upgrade to Operations" CTA. */
  requiresOperations?: boolean
  /**
   * `key` of another beta this one needs. While that beta is off, the toggle is replaced with a
   * "Requires <name>" button, and disabling that beta disables this one too.
   */
  requiresFeatureKey?: string
  /** Shown as "Learn More" while the feature is off. */
  learnMoreUrl?: string
  /** Shown as "Beta Guide" once the feature is on, and linked from the enable-success modal. */
  betaGuideUrl?: string
  /** Whether the "free during beta, priced differently later" disclosure applies. */
  freeDuringBeta: boolean
  /** "How it works" callout copy shown in the enable-success modal. */
  howItWorks?: string
  /** Disclosure checkboxes shown in the enable-confirm modal, in display order. */
  checkboxes: BetaFeatureCheckbox[]
}

export const BETA_FEATURE_CHECKBOXES = {
  accountWide: {
    id: 'account-wide',
    label: 'I understand opting into this beta will enable it for all users on my account.'
  },
  mayChange: {
    id: 'may-change',
    label: 'I understand this feature is in beta and may change without notice.'
  },
  incomplete: {
    id: 'incomplete',
    label: 'I understand this feature may have occasional issues or incomplete functionality.'
  },
  freeDuringBeta: {
    id: 'free-during-beta',
    label: 'I understand this feature is free during beta and will be priced differently in the future.'
  },
  freeDuringBetaMayBePriced: {
    id: 'free-during-beta',
    label: 'I understand this feature is free during beta and may be priced differently in the future.'
  }
} as const satisfies Record<string, BetaFeatureCheckbox>

function baseCheckboxes(freeDuringBeta: boolean): BetaFeatureCheckbox[] {
  const checkboxes: BetaFeatureCheckbox[] = [
    BETA_FEATURE_CHECKBOXES.accountWide,
    BETA_FEATURE_CHECKBOXES.mayChange,
    BETA_FEATURE_CHECKBOXES.incomplete
  ]
  if (freeDuringBeta) {
    checkboxes.push(BETA_FEATURE_CHECKBOXES.freeDuringBeta)
  }
  return checkboxes
}

export const betaFeatures: BetaFeatureConfig[] = [
  {
    key: 'estimate-v2',
    name: 'Estimate Redesign',
    description:
      'A redesigned estimate page with bulk product swapping, better work order layout options, and easier navigation while building quotes.',
    segmentKey: 'beta-estimate-v-2-w-o-gbb',
    flagKey: 'estimateNext',
    learnMoreUrl: 'https://help.paintscout.com/support/beta-guides/estimate-v2-beta-guide',
    betaGuideUrl: 'https://help.paintscout.com/support/beta-guides/estimate-v2-beta-guide',
    freeDuringBeta: false,
    howItWorks:
      'Estimates are now editable as soon as you open them, settings live in one place, and the sidebar is organized into cards. You can also swap products across the whole estimate in one step and customize your work order layout.',
    checkboxes: baseCheckboxes(false)
  },
  {
    key: 'tiered-packages',
    name: 'Tiered Packages',
    description:
      'Present Good/Better/Best options on one estimate, and let your customer pick the package that fits their needs.',
    // LD serves both `tieredPricing` and `tieredPricingProductSwap` to members of this segment.
    segmentKey: 'beta-tiered-packages-w-prod-swap',
    flagKey: 'tieredPricing',
    // Packages only render in the redesigned estimate editor.
    requiresFeatureKey: 'estimate-v2',
    learnMoreUrl: 'https://help.paintscout.com/support/beta-guides/tiered-packages-beta-guide',
    betaGuideUrl: 'https://help.paintscout.com/support/beta-guides/tiered-packages-beta-guide',
    freeDuringBeta: false,
    howItWorks:
      'Set up a template once in settings, then add packages to any estimate. The estimate you build becomes your Standard package, and Basic and Premium are priced up or down from it.',
    checkboxes: baseCheckboxes(false)
  },
  {
    key: 'multi-products-on-rate',
    name: 'Multiple Products on Rates',
    description: 'Add more than one default product to a single production rate, like a primer and a topcoat.',
    segmentKey: 'beta-multi-products-on-rate',
    flagKey: 'multiProductRates',
    learnMoreUrl: 'https://help.paintscout.com/support/beta-guides/multiple-products-on-rates',
    betaGuideUrl: 'https://help.paintscout.com/support/beta-guides/multiple-products-on-rates',
    freeDuringBeta: false,
    howItWorks: 'Go to your production rates in settings and add more than 1 default product — like primer and paint.',
    checkboxes: baseCheckboxes(false)
  },
  {
    key: 'ai-chat',
    name: 'AI Estimate Building',
    description:
      'A chat assistant built into estimates. Ask it to add, edit, or adjust line items and it makes the changes for you as you build.',
    segmentKey: 'beta-ai-chat',
    flagKey: 'chatWithEstimate',
    learnMoreUrl: 'https://help.paintscout.com/support/beta-guides/ai-estimate-assistant-beta-guide',
    betaGuideUrl: 'https://help.paintscout.com/support/beta-guides/ai-estimate-assistant-beta-guide',
    freeDuringBeta: true,
    howItWorks:
      'Open an estimate and look for the AI Chat Assistant button in the sidebar menu. Ask it for changes; like adding, editing or removing items.',
    checkboxes: baseCheckboxes(true)
  },
  {
    key: 'painter-accounts',
    name: 'Painter Accounts',
    description: 'Give each painter their own login to see the jobs assigned to them and the relevant scope of work.',
    segmentKey: 'beta-painter-accounts',
    flagKey: 'painterAccount',
    requiresOperations: true,
    learnMoreUrl: 'https://help.paintscout.com/support/beta-guides/painter-accounts-beta-guide',
    betaGuideUrl: 'https://help.paintscout.com/support/beta-guides/painter-accounts-beta-guide',
    freeDuringBeta: true,
    howItWorks:
      'Invite painters from User Management — each painter gets a login and can see only their assigned jobs.',
    checkboxes: baseCheckboxes(true)
  },
  {
    key: 'open-api',
    name: 'Open API',
    description:
      'Connect PaintScout to your own tools and workflows. Generate API keys to read and write your data programmatically and build custom integrations.',
    segmentKey: 'beta-open-api',
    flagKey: 'selfManagedApiKeys',
    learnMoreUrl: 'https://help.paintscout.com/support/beta-guides/openapi-beta-guide',
    betaGuideUrl: 'https://help.paintscout.com/support/beta-guides/openapi-beta-guide',
    freeDuringBeta: true,
    howItWorks:
      'Head to Settings → API Keys to generate an API key, then use it with our documented endpoints to sync estimates, jobs, and clients with your other systems.',
    checkboxes: baseCheckboxes(true)
  },
  {
    key: 'work-order-translations',
    name: 'Work Order Translations',
    description:
      'Instantly switch a work order into another language. French and Spanish are available now, with more to come.',
    segmentKey: 'beta-translations',
    // Deliberately not the `translations` flag itself, which serves an array of enabled language
    // codes (e.g. ['es','fr'], or [] for none — see getEnabledTranslationLangs), not a boolean —
    // reading it with `!!flags[flagKey]` would show the toggle as "on" for a non-empty array
    // that a company had previously been granted, even after disabling the beta here. This flag
    // exists only to gate the opt-in card and should be boolean true/false.
    // Lifecycle: once work-order-translations graduates out of beta, delete this entry (and the
    // `translationsBetaEnabled` LD flag + `beta-translations` segment) — the `translations` flag
    // that actually gates the feature is unaffected and stays.
    flagKey: 'translationsBetaEnabled',
    // ENG-1677: the LD targeting on `translations` isn't wired to `beta-translations` membership,
    // so this card's on/off state didn't actually control translated-download access — disabling
    // left it open, and enabling granted no languages at all. Force `translations` to every
    // supported language on enable and to an empty list on disable so the card's state is always
    // the real gate.
    cascadeFlag: {
      flagKey: 'translations',
      enabledValue: SUPPORTED_LANGS.filter((lang) => lang !== 'en'),
      disabledValue: []
    },
    learnMoreUrl: 'https://help.paintscout.com/support/beta-guides/translated-work-orders-beta-guide',
    betaGuideUrl: 'https://help.paintscout.com/support/beta-guides/translated-work-orders-beta-guide',
    freeDuringBeta: true,
    howItWorks:
      'Open a work order and use the new language dropdown in the top toolbar to switch between English, French, and Spanish.',
    checkboxes: [
      BETA_FEATURE_CHECKBOXES.accountWide,
      BETA_FEATURE_CHECKBOXES.mayChange,
      BETA_FEATURE_CHECKBOXES.incomplete,
      BETA_FEATURE_CHECKBOXES.freeDuringBetaMayBePriced
    ]
  },
  {
    key: 'presentations-v2',
    name: 'New Presentations',
    description:
      'A rebuilt presentation builder with flexible page layouts and content blocks for the quotes your customers see.',
    segmentKey: 'beta-presentations-v2',
    flagKey: 'presentationNext',
    // The AI presentation tools ship with the v2 builder, so opting in enrolls both.
    cascadeSegmentKeys: ['beta-ai-presentation'],
    learnMoreUrl: 'https://help.paintscout.com/support/beta-guides/presentations-v2-beta-guide#create-a-presentation',
    betaGuideUrl: 'https://help.paintscout.com/support/beta-guides/presentations-v2-beta-guide#create-a-presentation',
    freeDuringBeta: false,
    howItWorks:
      'Go to Settings → Presentations and click the 3-dot menu on an existing presentation to convert it to the new version. You can also start a new one from scratch or build one with AI. New presentations are labelled (Beta).',
    checkboxes: baseCheckboxes(false)
  }
]

/**
 * Whether a beta is on, given the client's LaunchDarkly flags. A comingSoon feature isn't actually
 * toggleable yet — never report it as enabled, even if its flagKey happens to be a pre-existing flag
 * that's already true for other reasons (e.g. estimateNext is also driven by the separate, unrelated
 * "New Estimate View" self-serve toggle).
 */
export function isBetaFeatureEnabled(feature: BetaFeatureConfig, flags: Record<string, any>): boolean {
  return !feature.comingSoon && !!feature.flagKey && !!flags[feature.flagKey]
}

/** Names of the betas that are on, in settings-page order — sent to Userflow as "Features Enabled". */
export function getEnabledBetaFeatureNames(flags: Record<string, any>): string[] {
  return betaFeatures.filter((feature) => isBetaFeatureEnabled(feature, flags)).map((feature) => feature.name)
}

/** The betas whose `requiresFeatureKey` is `key` — the ones that must turn off when `key` turns off. */
export function getDependentBetaFeatures(key: string): BetaFeatureConfig[] {
  return betaFeatures.filter((feature) => feature.requiresFeatureKey === key)
}
